import React, { useMemo, useState } from "react";
import { Badge, Card, Empty, Segmented, Skeleton } from "antd";
import { useTranslation } from "react-i18next";

import { type SupportRequestStatus } from "@fitness-app/data-models";

import { useIsPhone } from "~/hooks/useIsMobile";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { SupportRequestCard } from "~/modules/Support/components/SupportRequestCard";
import { SupportRequestDrawer } from "~/modules/Support/components/SupportRequestDrawer";
import { useTraineeSupportRequests } from "~/modules/Support/hooks/useTraineeSupportRequests";
import { CLOSED_SUPPORT_STATUSES } from "~/modules/Support/supportConstants";
import { SUPPORT_BOARD_FEATURE_FLAG } from "~/modules/Support/supportFeatureFlag";
import { useTraineeProfile } from "~/modules/Trainee/TraineeProfile/scope";
import { useTeamMemberTasksDueTodayOrPlannedCount } from "../../../Dashboard/TeamMemberTasks";
import { TeamMemberTaskList } from "../../../Dashboard/TeamMemberTasks/components/TeamMemberTaskList";
import { TeamMemberTaskProvider } from "../../../Dashboard/TeamMemberTasks/context/TeamMemberTaskContext";
import { LatestActivityContent } from "./LatestActivity";

type ViewType = "activity" | "tasks" | "support";
type SupportFilter = "open" | "closed";

type Props = {
  /** When set, caps the card height (px) so it matches the basic-info card next to it and scrolls internally. */
  maxHeight?: number;
};

export const TraineeActivityAndTasks: React.FC<Props> = ({ maxHeight }) => {
  const isPhone = useIsPhone();
  const { t } = useTranslation("trainees");
  const [rawView, setView] = useState<ViewType>("activity");
  const { data: profile } = useTraineeProfile();
  const { count: tasksDueTodayOrPlannedCount, overdueCount } = useTeamMemberTasksDueTodayOrPlannedCount(profile?.id);
  const { enabled: isSupportBoardEnabled } = usePostHogFeatureFlag(SUPPORT_BOARD_FEATURE_FLAG);
  const { requests: supportRequests, isLoading: supportRequestsLoading } = useTraineeSupportRequests(
    isSupportBoardEnabled ? profile?.id : null,
  );
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [supportFilter, setSupportFilter] = useState<SupportFilter>("open");
  // Flags load async — if the support flag turns off while the tab is selected, the
  // Segmented no longer offers it, so the view must fall back too.
  const view = !isSupportBoardEnabled && rawView === "support" ? "activity" : rawView;

  const { openSupportRequests, closedSupportRequests } = useMemo(
    () => ({
      openSupportRequests: supportRequests.filter(
        (request) => !CLOSED_SUPPORT_STATUSES.includes(request.status as SupportRequestStatus),
      ),
      closedSupportRequests: supportRequests.filter((request) =>
        CLOSED_SUPPORT_STATUSES.includes(request.status as SupportRequestStatus),
      ),
    }),
    [supportRequests],
  );
  const visibleSupportRequests = supportFilter === "open" ? openSupportRequests : closedSupportRequests;
  const selectedRequest = useMemo(
    () => supportRequests.find((request) => request.id === selectedRequestId) || null,
    [supportRequests, selectedRequestId],
  );

  return (
    <TeamMemberTaskProvider traineeId={profile?.id}>
      <Card
        className="activity-card h-full"
        style={maxHeight ? { maxHeight } : undefined}
        // TRE-2076: na telefonie 900 px to więcej niż ekran — lista wyglądała jak „nie scrolluje się".
        styles={maxHeight ? undefined : { body: { maxHeight: isPhone ? "55vh" : 900, overflow: "auto" } }}
      >
        <div className="mb-4">
          <Segmented
            value={view}
            onChange={(value) => setView(value as ViewType)}
            options={[
              { value: "activity", label: t("sections.latestActivity") },
              {
                value: "tasks",
                label: (
                  <span className="inline-flex items-center gap-2">
                    <span>{t("sections.tasks")}</span>
                    {tasksDueTodayOrPlannedCount > 0 ? (
                      <Badge
                        count={tasksDueTodayOrPlannedCount}
                        overflowCount={99}
                        size="small"
                        color={overdueCount > 0 ? "red" : "blue"}
                      />
                    ) : null}
                  </span>
                ),
              },
              ...(isSupportBoardEnabled
                ? [
                    {
                      value: "support",
                      label: (
                        <span className="inline-flex items-center gap-2">
                          <span>{t("sections.supportRequests")}</span>
                          {openSupportRequests.length > 0 ? (
                            <Badge count={openSupportRequests.length} overflowCount={99} size="small" color="blue" />
                          ) : null}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
            block
          />
        </div>

        {view === "activity" ? (
          <LatestActivityContent />
        ) : !profile ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : view === "tasks" ? (
          <TeamMemberTaskList
            traineeId={profile.id}
            trainee={{ id: profile.id, firstName: profile.firstName, lastName: profile.lastName, email: profile.email }}
            embedded
          />
        ) : supportRequestsLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <div className="space-y-3">
            <Segmented
              size="small"
              value={supportFilter}
              onChange={(value) => setSupportFilter(value as SupportFilter)}
              options={[
                { value: "open", label: `${t("supportRequests.open")} (${openSupportRequests.length})` },
                { value: "closed", label: `${t("supportRequests.closed")} (${closedSupportRequests.length})` },
              ]}
            />
            {visibleSupportRequests.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("supportRequests.empty")} />
            ) : (
              <div className="space-y-2">
                {visibleSupportRequests.map((request) => (
                  <SupportRequestCard
                    key={request.id}
                    request={request}
                    showStatus
                    onClick={() => setSelectedRequestId(request.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
      <SupportRequestDrawer request={selectedRequest} onClose={() => setSelectedRequestId(null)} />
    </TeamMemberTaskProvider>
  );
};
